import React, { useState, useContext } from 'react';
import { Helmet } from 'react-helmet';
import { useApolloClient } from '@apollo/client';
import { Box, Button, RadioCard, Tag, Flex, Text } from '@indeed/ifl-components';
import { usePageFlow } from '../../../lib/pageFlow/usePageFlow';
import {
    useGetGenderFormDataQuery,
    useUpdateLightApplyGenderInfoMutation,
} from './__generated__/GenderPage.generated';
import {
    updateSmartApplyContactInfoCache,
    buildCompleteContactInfoMutationInput,
    validateCacheServerConsistency,
    SmartApplyContactInfoUpdate,
} from '../../../lib/apollo/smartApplyStateCache';
import PageTransition from '../../../components/PageTransition/PageTransition';
import FormHeader from '../components/FormHeader/FormHeader';
import { NodeIdContext } from '../../../lib/context/NodeIdContext';
import { useSearchParams } from 'react-router-dom';

/**
 * GenderPage with Reactive Apollo Cache Updates
 *
 * This approach:
 * 1. ✅ Writes directly to SmartApplyApplicationState cache (no separate local state)
 * 2. ✅ Partial cache writes on field changes (immediate UI updates)
 * 3. ✅ Single server mutation on "Continue" (bulk save)
 * 4. ✅ Zero useState complexity
 * 5. ✅ Uses OneGraph schema as the single source of truth
 * 6. ✅ Properly fetches gender from localStorage in local mock mode
 */
const GenderPage: React.FC = () => {
    const { goForward, loading: pageFlowLoading } = usePageFlow();
    const apolloClient = useApolloClient();

    const [searchParams] = useSearchParams();
    const indeedApplyableJobId = searchParams.get('indeedApplyableJobId') || '';
    // Get nodeId from context
    const nodeId = useContext(NodeIdContext);

    // 1. Get server data (SmartApply + ApplyParameters gender config) - Apollo should auto-update on cache changes
    const { data, loading } = useGetGenderFormDataQuery({
        variables: {
            nodeId: nodeId || '', // Provide empty string as fallback for TypeScript
            indeedApplyableJobId, // Pass job ID to get gender configuration from applyParameters
        },
        skip: !nodeId || !indeedApplyableJobId, // Skip query when either parameter is missing
        errorPolicy: 'all',
        // Cache-first: return cache data immediately, only fetch if not in cache
        fetchPolicy: 'cache-first',
    });

    // 2. Server mutation for bulk save on Continue
    const [updateServerGenderInfo, { loading: saving }] = useUpdateLightApplyGenderInfoMutation();

    // 3. Extract current state from Apollo data - should auto-update when data changes
    const smartApplyState =
        data?.smartApplyApplicationState?.__typename === 'SmartApplyApplicationState'
            ? data.smartApplyApplicationState
            : null;

    const contactInfo = smartApplyState?.contactInfo;

    // For gender, it's typically optional but recommended by companies
    const isGenderRequired = false; // Gender is typically optional

    // 4. Handle form field changes with direct cache writes - Apollo should auto-update
    const handleFieldChange = (field: keyof SmartApplyContactInfoUpdate, value: string) => {
        // Prevent cache updates while data is still loading
        if (loading) {
            return;
        }

        if (!smartApplyState?.id) {
            console.warn('No SmartApplyApplicationState ID available for cache update');
            return;
        }

        // Use cache.modify() - more flexible, no fragment validation issues
        const updatedContactInfo = updateSmartApplyContactInfoCache(
            apolloClient.cache,
            smartApplyState.id,
            { [field]: value },
        );

        if (!updatedContactInfo) {
            console.error(`Failed to update ${field}`);
        }
    };

    // State to track when user has attempted to continue (for validation UX)
    const [, setHasAttemptedContinue] = useState(false);

    const handleGenderSelect = (genderValue: string) => {
        // Update gender in cache immediately
        handleFieldChange('gender', genderValue);
    };

    // 5. Handle Continue - flush all changes to server
    const handleContinue = async () => {
        if (!smartApplyState?.id || !contactInfo) {
            console.error('Cannot save: missing SmartApplyApplicationState ID or contact info');
            return;
        }

        // Validation - require gender selection based on standard behavior
        const hasRequiredFields = !!(!isGenderRequired || contactInfo.gender?.trim());

        if (!hasRequiredFields) {
            // Show validation errors now that user has attempted to continue
            setHasAttemptedContinue(true);
            return;
        }

        // ✅ CRITICAL: Build complete mutation input to prevent server data loss
        // The server does NOT support partial updates - must include ALL contactInfo fields
        const completeMutationInput = buildCompleteContactInfoMutationInput(
            contactInfo, // Complete current contactInfo from cache
            {
                // Only the field we want to update (but all existing fields preserved)
                gender: contactInfo.gender,
            },
        );

        const mutationVariables = {
            input: { id: smartApplyState.id },
            updateContactInfoInput: completeMutationInput,
        };

        try {
            // Send complete contactInfo to server (all fields included)
            await updateServerGenderInfo({
                variables: mutationVariables,
                // ✅ Validate that server preserved all data
                update: (cache, { data: mutationData }) => {
                    if (mutationData?.smartApplyApplicationState?.updateContactInfo?.state) {
                        const serverContactInfo =
                            mutationData.smartApplyApplicationState.updateContactInfo.state;

                        // ✅ Validate that server returned ALL fields we sent (data preservation check)
                        validateCacheServerConsistency(
                            completeMutationInput,
                            serverContactInfo,
                            'UpdateLightApplyGenderInfo',
                        );

                        console.log(
                            '✅ Gender info successfully saved with complete data preservation',
                        );
                        void goForward();
                    }
                },
            });
        } catch (error) {
            console.error('Failed to save gender info:', error);
            // Could show error message to user
        }
    };

    const handleNoAnswer = () => {
        // Update gender in cache immediately and continue
        handleFieldChange('gender', 'no-answer');

        // Proceed immediately since "no answer" is a valid selection
        setTimeout(() => handleContinue(), 100); // Small delay to ensure cache update
    };

    // 6. Form validation and dirty state from cache
    const canContinue = !!(!isGenderRequired || contactInfo?.gender?.trim());

    if (loading) {
        return (
            <PageTransition pageKey="contact-info-gender">
                <Helmet>
                    <title>性別を選択してください - Light Apply - Indeed</title>
                </Helmet>

                <Box sx={{ padding: '32px 16px', textAlign: 'center' }}>
                    <Text>プロフィール情報を読み込んでいます...</Text>
                </Box>
            </PageTransition>
        );
    }

    return (
        <PageTransition pageKey="contact-info-gender">
            <Helmet>
                <title>性別を選択してください - Light Apply - Indeed</title>
            </Helmet>

            <Box sx={{ inlineSize: '100%' }}>
                <FormHeader
                    step={4}
                    total={5}
                    title="性別を選択してください"
                    additionalElement={
                        <Tag variant="light" sx={{ fontWeight: 'bold', fontSize: '12px' }}>
                            採用企業により入力推奨
                        </Tag>
                    }
                />

                {/* Form Fields - Reading from SmartApplyApplicationState cache */}
                <Box
                    sx={{
                        marginBlockEnd: '32px',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '32px',
                    }}
                >
                    {/* Gender Selection */}
                    <Flex
                        sx={{
                            marginBlockEnd: '24px',
                            gap: '16px',
                            flexDirection: 'row',
                            inlineSize: '100%',
                        }}
                    >
                        <Box sx={{ flex: 1 }}>
                            <RadioCard
                                name="gender"
                                value="male"
                                checked={contactInfo?.gender === 'male'}
                                label="男性"
                                onClick={() => handleGenderSelect('male')}
                                sx={{ inlineSize: '100%', blockSize: '100%' }}
                            />
                        </Box>
                        <Box sx={{ flex: 1 }}>
                            <RadioCard
                                name="gender"
                                value="female"
                                checked={contactInfo?.gender === 'female'}
                                label="女性"
                                onClick={() => handleGenderSelect('female')}
                                sx={{ inlineSize: '100%', blockSize: '100%' }}
                            />
                        </Box>
                    </Flex>

                    {/* Action Buttons */}
                    <Box>
                        <Button
                            variant="primary"
                            size="lg"
                            sx={{ inlineSize: '100%', marginBlockEnd: '8px' }}
                            onClick={handleContinue}
                            disabled={!canContinue || saving || pageFlowLoading}
                        >
                            {saving ? '保存中...' : pageFlowLoading ? '読み込み中...' : '次へ'}
                        </Button>

                        <Button
                            variant="tertiary"
                            size="lg"
                            sx={{ inlineSize: '100%' }}
                            onClick={handleNoAnswer}
                            disabled={saving || pageFlowLoading}
                        >
                            回答しない
                        </Button>
                    </Box>
                </Box>
            </Box>
        </PageTransition>
    );
};

export default GenderPage;
